import React, { useEffect } from 'react';
import { LocalDiningHistoryItem } from './LocalDiningHistoryItem';
import { createStyles } from 'utils/createStyle';
import Skeleton from 'react-loading-skeleton';
import { useLocalDiningHistory } from 'common/api/clo-history/mock-api/useLocalDiningHistory';

const style = createStyles({
    container: 'mt-4',
    datePickerContainer: 'block md:flex md:flex-col lg:items-end md:ml-auto',
    contentContainer: '',
    loadMore: [
        'py-2 px-10 rounded-3xl bg-brand text-white font-semibold mt-8',
        { fontSize: 18, lineHeight: '20px' },
    ],
    buttonRow: ['text-center'],
});

export interface LocalDiningHistoryProps {
    dateRange?:
        | {
              start: Date;
              end: Date;
          }
        | undefined
        | null;
}

export const LocalDiningHistory = (props: LocalDiningHistoryProps) => {
    const { error, loading, results, setViewedDateRange, loadMore, canLoadMoreResults } =
        useLocalDiningHistory();

    useEffect(() => {
        if (props.dateRange) {
            const { start, end } = props.dateRange;
            setViewedDateRange({ start: start.toISOString(), end: end.toISOString() });
        }
    }, [props.dateRange]);

    const renderBody = () => {
        if (error) {
            return <LocalDiningHistoryLoadingError />;
        }

        if (loading) {
            return <LocalDiningHistoryLoader />;
        }

        if (results) {
            if (results.length === 0) {
                return <LocalDiningHistoryNoOrders />;
            }

            return (
                <>
                    {results.map((result) => {
                        return <LocalDiningHistoryItem key={result.transactionId} item={result} />;
                    })}

                    {canLoadMoreResults && (
                        <div className={style.buttonRow}>
                            <button className={style.loadMore} onClick={loadMore}>
                                Load More Items
                            </button>
                        </div>
                    )}
                </>
            );
        }
    };

    return (
        <div className={style.container}>
            <div className={style.contentContainer}>{renderBody()}</div>
        </div>
    );
};

const noOrdersStyle = createStyles({
    header: 'text-brand-dark font-semibold text-lg mb-2.5 text-xl',
    loading: ['w-full mb-4 text-base', { height: '162px', fontSize: '16px' }],
});

export const LocalDiningHistoryLoader = () => {
    return (
        <>
            <Skeleton className={noOrdersStyle.loading} />
            <Skeleton className={noOrdersStyle.loading} />
            <Skeleton className={noOrdersStyle.loading} />
        </>
    );
};

export const LocalDiningHistoryLoadingError = () => {
    return (
        <div>
            <h3 className={noOrdersStyle.header}>Something went wrong</h3>

            <p>
                We’re sorry, your order history for local dining is currently unavailable. Please
                try refreshing the page.
            </p>
        </div>
    );
};

export const LocalDiningHistoryNoOrders = () => {
    return (
        <div>
            <h3 className={noOrdersStyle.header}>No orders yet</h3>

            <p>
                Only available on the RaiseRight app. Earn 5% for your organization every time you
                dine at participating local restaurants and pay with a linked credit or debit card.
            </p>
        </div>
    );
};
